import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageFile, MessageFileItem } from '../../type/chat.types';
import { idGenerator } from '../../utils/utils';
import { sendFile } from '../../api/chat.api';
import { immediateToast } from 'utils/toast';

// Custom hook for handling input-related functionality
const useInput = (props: any) => {
    const {
        inputProps,
        editingMessage,
        sentMessage,
        setIsMessageError,
        fileUploadUrl,
        isMessageError,
        fileSizeLimit = 0,
        isSending,
        isNewTicket,
        onSend,
        onResend,
        onEdit,
        onCancelEdit,
        onCancel,
        onErrorFile,
        replyTo,
        onCancelReply,
    } = props;

    const inputRef = useRef<HTMLTextAreaElement>(null);

    const [currentMessage, setCurrentMessage] = useState<string>('');
    const [attachmentData, setAttachmentData] = useState<MessageFile>([]);
    const pendingFilesCount = attachmentData.filter(
        (file) => file?.status === 'loading',
    ).length;
    const [disabled, setDisabled] = useState<boolean>(false);
    const [messageError, setMessageError] = useState<boolean>(false);
    const [showHelper, setShowHelper] = useState<boolean>(false);
    const [msgError, setMsgError] = useState<string>('');
    const [serverError, setServerError] = useState<string>('');
    const [validMaxLength, setValidMaxLength] = useState<boolean>(true);
    const [validMinLength, setValidMinLength] = useState<boolean>(true);

    // handle message got error or not and handle input is disable or not
    const handleDisabled = () => {
        isMessageError ? setMessageError(true) : setMessageError(false);
        isSending ? setDisabled(true) : setDisabled(false);
    };

    useEffect(() => {
        handleDisabled();
    }, [isMessageError, isSending]);

    // Effect to update the size of the input field based on the text
    useEffect(() => {
        if (currentMessage && inputRef.current) {
            const rows = currentMessage.split('\n').length;
            inputRef.current.rows = rows;
        }
    }, [currentMessage]);

    // handle max and min valid length of message
    useEffect(() => {
        if (inputProps?.validLength) {
            setValidMaxLength(
                currentMessage?.trim().length <= inputProps?.validLength[1],
            );
            setValidMinLength(
                currentMessage?.trim()?.length >= inputProps?.validLength[0],
            );
        }
    }, [inputProps?.validLength, currentMessage]);

    // handle server errors
    useEffect(() => {
        setServerError(inputProps?.error);
    }, [inputProps?.error]);

    // remove server error text on change
    useEffect(() => {
        inputProps?.error && setServerError('');
    }, [currentMessage]);

    // Effect to update the text of length error
    useEffect(() => {
        const isLengthInvalid = !validMinLength || !validMaxLength;
        const hasValidationError = serverError || isLengthInvalid;

        let errorMessage = '';

        if (serverError) {
            errorMessage = serverError;
        } else if (showHelper && isLengthInvalid) {
            const [minLength, maxLength] = inputProps?.validLength || [];
            errorMessage = `طول متن وارد شده باید بین ${minLength} و ${maxLength} کارکتر باشد.`;
        }

        if (hasValidationError) {
            setMsgError(errorMessage);
        } else {
            setMsgError('');
        }
    }, [
        showHelper,
        validMinLength,
        validMaxLength,
        currentMessage,
        serverError,
        inputProps?.validLength,
    ]);

    // handle file attachment
    const handleProcessFiles = (uploadedFiles: FileList | File[]) => {
        const MAX_FILE_SIZE = fileSizeLimit * 1024 * 1024;
        const allowedFileExtensions = new Set([
            'png',
            'jpeg',
            'jpg',
            'zip',
            'rar',
            'doc',
            'docx',
            'xls',
            'xlsx',
            'pdf',
            'txt',
        ]);

        const validFiles: File[] = [];
        const invalidFiles: Array<{ file: File; type: 'size' | 'format' }> = [];

        Array.from(uploadedFiles).forEach((file) => {
            const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

            if (!allowedFileExtensions.has(extension)) {
                invalidFiles.push({ file, type: 'format' });
            } else if (file.size > MAX_FILE_SIZE) {
                invalidFiles.push({ file, type: 'size' });
            } else {
                validFiles.push(file);
            }
        });

        if (invalidFiles.length > 0) {
            onErrorFile?.(invalidFiles);
        }

        if (validFiles.length > 0) {
            const newFiles = validFiles.map((file) => ({
                file,
                local_id: idGenerator(),
                status: 'loading',
            }));

            setAttachmentData((prevFiles) => [...prevFiles, ...newFiles]);

            newFiles.forEach((file: MessageFileItem) => {
                handleSendFileToServer(file);
            });
        }
    };

    // handle file attachment click
    const handleAttach = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.png,.jpeg,.jpg,.zip,.rar,.doc,.docx,.xls,.xlsx,.pdf,.txt';
        fileInput.multiple = true;

        fileInput.addEventListener('change', (event: any) => {
            const files = (event.target as HTMLInputElement).files;
            if (files) {
                handleProcessFiles(files);
            }
        });

        fileInput.click();
    };

    // send each file to server for uploading step (first step)
    const handleSendFileToServer = async (media: MessageFileItem) => {
        try {
            const res = await sendFile({
                url: fileUploadUrl,
                file: media?.file,
                local_id: Number(media?.local_id),
            });

            if (res) {
                // replace data from server with local one
                setAttachmentData((prevData: MessageFile) =>
                    prevData?.map((item: MessageFileItem) => {
                        return item.local_id === media?.local_id
                            ? {
                                  file: res,
                                  local_id: media?.local_id,
                                  status: 'success',
                              }
                            : item;
                    }),
                );
            } else {
                immediateToast('error', {
                    message: res,
                    timeout: 3000,
                });

                throw new Error(res);
            }
        } catch (error) {
            // update data with error status
            setAttachmentData((prevData: MessageFile) =>
                prevData?.map((item: MessageFileItem) => {
                    return item.local_id === media?.local_id
                        ? {
                              file: media?.file,
                              local_id: media?.local_id,
                              status: 'error',
                          }
                        : item;
                }),
            );
        }
    };

    // Default behavior for handling the message (to be overridden by the parent component)
    const handleSendMessage = async () => {
        console.log("Firing default behavior of chat input's function");
    };

    // resend file when it got error during uploading
    const handleResendFile = (file: MessageFileItem) => {
        //  set loading for resended file
        setAttachmentData((prevFiles) =>
            prevFiles.map((fileItem) =>
                fileItem.local_id === file.local_id
                    ? { ...fileItem, status: 'loading' }
                    : fileItem,
            ),
        );

        // resend file for upload
        handleSendFileToServer({ ...file, status: 'loading' });
    };

    // Function to handle deleting a file from the attachment list
    const handleDeleteFile = (localId: number) => {
        const newArray = attachmentData.filter(
            (file) => file?.local_id !== localId,
        );
        setAttachmentData(newArray);

    };

    // check if sentMessage is exicted to update current message and attached file
    useEffect(() => {
        if (sentMessage) {
            setCurrentMessage(sentMessage?.text ?? '');

            if (sentMessage?.files?.length > 0) {
                const newFiles = sentMessage?.files?.map((file: any) => {
                    const isTypeString = typeof file === 'string';

                    const fileItem = {
                        file: !isTypeString ? file?.file : file,
                        local_id: handleExtractLocalId(
                            !isTypeString ? file?.file : file,
                        ),
                        status: 'success',
                    };

                    return fileItem;
                });

                setAttachmentData(newFiles);
            }
        } else {
            handleDisabled();
            setCurrentMessage('');
            setAttachmentData([]);
        }
    }, [sentMessage]);

    // clear message on cancel edit
    const handleCancelEdit = useCallback(() => {
        onCancelEdit();
        handleDisabled();
        setCurrentMessage('');
        setAttachmentData([]);
        setIsMessageError(false);
    }, [editingMessage, sentMessage]);

    // send message
    const handleSend = () => {
        if (isNewTicket && onSend && !sentMessage) {
            onSend(
                currentMessage,
                setCurrentMessage,
                attachmentData,
                setAttachmentData,
                undefined,
                replyTo?.id ?? null,
            );
            onCancelReply?.();
            return;
        }

        if (validMinLength && validMaxLength) {
            if (onSend && !sentMessage) {
                onSend(
                    currentMessage,
                    setCurrentMessage,
                    attachmentData,
                    setAttachmentData,
                    undefined,
                    replyTo?.id ?? null,
                );
                onCancelReply?.();
                setShowHelper(false);
            } else if (onSend && sentMessage) {
                onSend(
                    currentMessage,
                    setCurrentMessage,
                    attachmentData,
                    setAttachmentData,
                    sentMessage,
                    sentMessage?.parent_id ?? null,
                );
            } else {
                handleSendMessage();
            }
        } else {
            !showHelper && setShowHelper(true);
        }
    };

    // handle Resend failed message
    const handleResendMessage = () => {
        onResend();
    };

    // handle Edit failed message
    const handleEditMessage = () => {
        onEdit(sentMessage);
        setDisabled(false);
        setMessageError(false);
    };

    // handle Cancel failed message
    const handleCancelMessage = () => {
        onCancel();
        setDisabled(false);
        setMessageError(false);
        setIsMessageError(false);
    };

    // handle get file name out of ur
    const getFileNameFromURL = (media: any): string | undefined => {
        const file =
            typeof media.file === 'string' ? media.file : media.file?.name;

        if (!file || typeof file !== 'string') {
            return undefined;
        }

        // Get only the file name (with extension) after the last slash
        const lastSlashIndex = file.lastIndexOf('/');
        return lastSlashIndex !== -1
            ? file.substring(lastSlashIndex + 1)
            : file;
    };

    // take out the format out of text
    const handleGetFileFormat = (file: string) => {
        var lastDotIndex = file?.lastIndexOf('.');

        if (lastDotIndex !== -1) {
            var result = file?.substring(lastDotIndex + 1);

            return result;
        } else {
            return file;
        }
    };

    function handleExtractLocalId(url: string): number {
        const lastSlashIndex = url.lastIndexOf('/');
        const partAfterLastSlash = url.substring(lastSlashIndex + 1);

        const firstDotIndex = partAfterLastSlash.indexOf('.');
        const localId = partAfterLastSlash.substring(0, firstDotIndex);

        return Number(localId) ?? 0;
    }

    // Return the values and functions to be used by the parent component
    return {
        inputRef,
        currentMessage,
        attachmentData,
        editingMessage,
        pendingFilesCount,
        lengthError: msgError,
        fileUploadUrl,
        fileSizeLimit,

        setCurrentMessage,
        getFileNameFromURL,
        onGetFileFormat: handleGetFileFormat,
        onAttach: handleAttach,
        onProcessFiles: handleProcessFiles,
        onResendFile: handleResendFile,
        onSend: handleSend,
        onResend: handleResendMessage,
        onDeleteFile: handleDeleteFile,
        onCancelEdit: handleCancelEdit,
        onEdit: props?.onEdit ? handleEditMessage : undefined,
        onCancel: handleCancelMessage,
        onSendMessage: handleSendMessage,

        isDisabled: disabled,
        isNewTicket: isNewTicket,
        isMessageError: messageError,
        placeHolderText: inputProps?.placeHolderText,
        fileHolderText: inputProps?.fileHolderText,
        submitLabel: inputProps?.submitLabel,
        isSendingFile: inputProps?.isSendingFile,
        smallScreen: inputProps?.smallScreen,
        shadow: inputProps?.shadow,
        invisible: inputProps?.invisible,
        borderRadius: inputProps?.borderRadius,
        isSending,
        replyTo,
        onCancelReply,
    };
};

export default useInput;
